import React, { useState } from 'react';
import { TabList, TabPanel, Tabs } from '@repo/design-system-kit';
import { FormattedMessage, useIntl } from 'react-intl';
import cx from 'clsx';
import { IconLockCategory } from '@repo/monochrome-icons';
import { SidePanel } from 'receivable-invoices-js/react/components/client-portal/side-panel';
import { Card } from 'receivable-invoices-js/react/components/client-portal/receivable-document-card/card';
import {
  getInvoiceCardDate,
  getInvoices,
  getQuoteCardDate,
  getQuotes,
} from 'receivable-invoices-js/react/utils/client-portal/utils';
import type { ClientPortal } from 'receivable-invoices-js/react/api/models/client-portal';
import type { Quote } from 'receivable-invoices-js/react/api/models/quote';
import type { ReceivableInvoice } from 'receivable-invoices-js/react/api/models/receivable-invoice';
import styles from './client-portal.module.css';

type TabType = 'quotes' | 'invoices';

export interface ClientPortalProps {
  clientPortalData: ClientPortal;
  quotesData: Quote[];
  invoicesData: ReceivableInvoice[];
  initialTab?: TabType;
}

export function ClientPortal({
  clientPortalData,
  quotesData = [],
  invoicesData = [],
  initialTab = 'quotes',
}: ClientPortalProps): React.ReactNode {
  const { formatMessage } = useIntl();

  const [activeTab, setActiveTab] = useState<TabType>(initialTab);

  const { organization, client } = clientPortalData;

  const tabs: { id: string; title: string }[] = [];

  if (quotesData.length) {
    tabs.push({
      id: 'quotes',
      title: formatMessage({ id: 'client-portal.tabs.quotes' }),
    });
  }

  if (invoicesData.length) {
    tabs.push({
      id: 'invoices',
      title: formatMessage({ id: 'client-portal.tabs.invoices' }),
    });
  }

  const handleTabChange = (tabId: React.Key): void => {
    setActiveTab(tabId as TabType);
  };

  const shouldDisplayEmptyState = quotesData.length === 0 && invoicesData.length === 0;

  const clientName = client.companyName.length > 0 ? client.companyName : client.firstName;

  const filteredInvoices = getInvoices(invoicesData);
  const filteredQuotes = getQuotes(quotesData);
  const shouldRenderNeedAttentionSection =
    filteredInvoices.attention.length > 0 || filteredQuotes.attention.length > 0;

  const shouldRenderCompletedSection =
    filteredInvoices.completed.length > 0 || filteredQuotes.completed.length > 0;

  return (
    <div
      className={styles.container}
      data-test-client-portal
      style={
        organization.colorTheme
          ? ({
              '--org-border-width': '8px',
              '--org-border-color': organization.colorTheme,
            } as React.CSSProperties)
          : undefined
      }
    >
      <SidePanel client={client} organization={organization} />
      {shouldDisplayEmptyState ? (
        <main className={styles.emptyState} data-test-empty-state>
          <h2 className="title-2" data-test-empty-state-title>
            <FormattedMessage id="client-portal.empty-state.title" />
          </h2>
          <p className="body-2" data-test-empty-state-subtitle>
            <FormattedMessage
              id="client-portal.empty-state.subtitle"
              values={{ companyName: organization.legalName }}
            />
          </p>
        </main>
      ) : (
        <main className={styles.content}>
          <div className={styles.nonDesktopHeader}>
            {Boolean(organization.avatarUrl) && (
              <div className={styles.logoContainer}>
                <img
                  alt={organization.legalName}
                  data-test-logo-image
                  src={organization.avatarUrl}
                />
              </div>
            )}
            <div>
              <h2 className="title-2">{organization.legalName}</h2>
              <p className={cx(styles.secondary, 'title-4')} data-test-client-name>
                <FormattedMessage id="client-portal.title.client-name" values={{ clientName }} />
              </p>
            </div>
          </div>
          <div className={styles.mainContainer}>
            {Boolean(shouldRenderNeedAttentionSection) && (
              <div className={styles.history} data-test-needs-attention-section>
                <h3 className="title-3" data-test-needs-attention-title>
                  <FormattedMessage id="client-portal.section.needs-attention" />
                </h3>
                <div className={styles.tabBody}>
                  {filteredInvoices.attention.map(invoice => {
                    const cardDate = getInvoiceCardDate(invoice);
                    return (
                      <Card
                        amount={invoice.amountDue?.value ?? null}
                        currency={invoice.amountDue?.currency ?? null}
                        date={cardDate || null}
                        documentId={invoice.id}
                        documentNumber={invoice.number || null}
                        isQuote={false}
                        key={invoice.number || invoice.id}
                        status={invoice.status}
                      />
                    );
                  })}

                  {filteredQuotes.attention.map(quote => {
                    const cardDate = getQuoteCardDate(quote);
                    return (
                      <Card
                        amount={quote.amountDue.value || null}
                        currency={quote.amountDue.currency || null}
                        date={cardDate || null}
                        documentId={quote.id}
                        documentNumber={quote.number || null}
                        isQuote
                        key={quote.number || quote.id}
                        quoteVersions={quote.quoteVersions}
                        status={quote.status}
                      />
                    );
                  })}
                </div>
              </div>
            )}

            {Boolean(shouldRenderCompletedSection) && (
              <div className={styles.history} data-test-needs-completed-section>
                <h3 className="title-3" data-test-completed-title>
                  <FormattedMessage id="client-portal.section.completed" />
                </h3>
                <Tabs activeId={activeTab} changeTab={handleTabChange} className={styles.tab}>
                  <TabList className={styles.tabHeader} tabs={tabs} />
                  {tabs.map(tab => (
                    <TabPanel className={styles.tabPanel} key={tab.id} tabId={tab.id}>
                      {tab.id === 'quotes' && quotesData.length > 0 ? (
                        <div className={styles.tabBody} data-test-quotes-content>
                          {filteredQuotes.completed.map(quote => {
                            const cardDate = getQuoteCardDate(quote);
                            return (
                              <Card
                                amount={quote.amountDue.value || null}
                                currency={quote.amountDue.currency || null}
                                date={cardDate || null}
                                documentId={quote.id}
                                documentNumber={quote.number || null}
                                isQuote
                                key={quote.number || quote.id}
                                quoteVersions={quote.quoteVersions}
                                status={quote.status}
                              />
                            );
                          })}
                        </div>
                      ) : null}

                      {tab.id === 'invoices' && invoicesData.length > 0 ? (
                        <div className={styles.tabBody} data-test-invoices-content>
                          {filteredInvoices.completed.map(invoice => {
                            const cardDate = getInvoiceCardDate(invoice);
                            return (
                              <Card
                                amount={invoice.amountDue?.value || null}
                                currency={invoice.amountDue?.currency || null}
                                date={cardDate || null}
                                documentId={invoice.id}
                                documentNumber={invoice.number || null}
                                isQuote={false}
                                key={invoice.number || invoice.id}
                                status={invoice.status}
                              />
                            );
                          })}
                        </div>
                      ) : null}
                    </TabPanel>
                  ))}
                </Tabs>
              </div>
            )}
          </div>
          <div className={styles.nonDesktopFooter}>
            <p className={cx(styles.secondary, 'body-1')} data-test-tax-number>
              <FormattedMessage id="client-portal.tax-number" /> {organization.legalNumber}
            </p>
            <a
              className={cx(styles.secondary, styles.poweredLink, 'caption')}
              data-test-powered-by
              href="https://qonto.com/"
            >
              <IconLockCategory height={12} width={12} />
              <FormattedMessage id="client-portal.powered-by" />
              {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx -- "Qonto" is a brand name  */}
              <strong>Qonto</strong>
            </a>
          </div>
        </main>
      )}
    </div>
  );
}
